import type {
  CompanyNews,
  CompanyProfile,
  CompetitorRef,
  DiscoveryStatusResponse,
  DiscoveryView,
} from "@scoach/types";
import { Button, SegmentedControl, Spinner } from "@scoach/ui";
import {
  Bolt,
  Chart,
  Doc,
  Globe,
  Inbox,
  Link as LinkIcon,
  Notebook,
  Spark,
  Trend,
  User,
  Users,
} from "@scoach/ui/icons";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { isDriveUrl, runDriveImport } from "../../lib/driveImport.ts";
import { safeHref } from "../../lib/safeHref.ts";
import { CompanyReportPanel } from "../companies/CompanyReportPanel.tsx";
import { CompanyLogo } from "./CompanyLogo.tsx";
import { DiscoveryProgress } from "./DiscoveryProgress.tsx";
import { discoveryApi } from "./api.ts";
import { faviconFor, hostOf } from "./favicon.ts";

type Tab = "overview" | "intel" | "notes" | "docs";

/** Small favicon that hides itself if it fails to load. */
function Favicon({ url, size = 18 }: { url?: string; size?: number }) {
  const [failed, setFailed] = useState(false);
  const src = faviconFor(url);
  if (!src || failed) return null;
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      onError={() => setFailed(true)}
      style={{ width: size, height: size, borderRadius: 4, flex: "0 0 auto" }}
    />
  );
}

function Section({
  title,
  icon,
  children,
}: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="disc-card">
      <div className="disc-card-head">
        {icon}
        <h3>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Fact({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <span className="disc-fact">
      {icon}
      <span>
        <span style={{ fontWeight: 700 }}>{value}</span>{" "}
        <span className="disc-fact-lbl">{label}</span>
      </span>
    </span>
  );
}

function Chips({ items }: { items: string[] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {items.map((c) => (
        <span key={c} className="ent-pill">
          {c}
        </span>
      ))}
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map((t) => (
        <li key={t} style={{ fontSize: 13.5, color: "var(--text-2)", lineHeight: 1.5 }}>
          {t}
        </li>
      ))}
    </ul>
  );
}

function NewsList({ items }: { items: CompanyNews[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map((n) => (
        <a
          key={n.title}
          href={safeHref(n.url) ?? "#"}
          target="_blank"
          rel="noreferrer"
          className="disc-news-item"
        >
          <Favicon url={n.url ?? n.source} size={20} />
          <div style={{ minWidth: 0 }}>
            <div className="disc-news-title">{n.title}</div>
            <div className="disc-news-meta">
              {[n.source || hostOf(n.url), n.date].filter(Boolean).join(" · ")}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

/** Founder headshot that falls back to initials if the photo fails to load
 * (many photoUrls 404 — otherwise the browser shows a broken-image icon). */
function FounderAvatar({ name, photoUrl }: { name: string; photoUrl?: string }) {
  const [failed, setFailed] = useState(false);
  if (!photoUrl || failed) return <div className="disc-avatar">{initials(name)}</div>;
  return (
    <img
      src={photoUrl}
      alt={name}
      width={48}
      height={48}
      onError={() => setFailed(true)}
      style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flex: "0 0 auto" }}
    />
  );
}

export function DiscoveryScreen({ discoveryId }: { discoveryId: string }) {
  const nav = useNavigate();
  const [view, setView] = useState<DiscoveryView | null>(null);
  const [status, setStatus] = useState<DiscoveryStatusResponse | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [freeText, setFreeText] = useState("");
  const [notes, setNotes] = useState("");
  const [starting, setStarting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [docErr, setDocErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const profile: CompanyProfile | null = view?.profile ?? status?.profile ?? null;
  const discovery = view?.discovery ?? null;
  const errored = status?.status === "error";
  const running = !profile && !errored;

  useEffect(() => {
    let alive = true;
    discoveryApi
      .get(discoveryId)
      .then((v) => {
        if (!alive) return;
        setView(v);
        setFreeText(v.discovery.freeText ?? "");
        setNotes(v.discovery.notes ?? "");
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [discoveryId]);

  const haveProfile = !!view?.profile;
  useEffect(() => {
    if (haveProfile) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const s = await discoveryApi.status(discoveryId);
        if (!alive) return;
        setStatus(s);
        if (s.status === "running") {
          timer = setTimeout(poll, 1600);
        } else if (s.profile) {
          discoveryApi
            .get(discoveryId)
            .then((v) => alive && setView(v))
            .catch(() => {});
        }
      } catch {
        if (alive) timer = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [discoveryId, haveProfile]);

  function saveNotes() {
    void discoveryApi.patch(discoveryId, { freeText, notes }).catch(() => {});
  }

  async function start(mode: "live" | "setup") {
    setStarting(true);
    try {
      const { meetingId } = await discoveryApi.startMeeting(discoveryId, mode);
      nav({
        to: mode === "live" ? "/meetings/$id/live" : "/meetings/$id/setup",
        params: { id: meetingId },
      });
    } finally {
      setStarting(false);
    }
  }

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setUploading(true);
    setDocErr(null);
    try {
      const r = await discoveryApi.uploadAttachments(discoveryId, files);
      setView((v) => (v ? { ...v, discovery: { ...v.discovery, attachments: r.attachments } } : v));
    } catch (err) {
      setDocErr((err as Error).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onImportLink() {
    const url = linkUrl.trim();
    if (!url) return;
    if (!isDriveUrl(url)) {
      setDocErr("Enter a Google Docs/Drive link");
      return;
    }
    setImporting(true);
    setDocErr(null);
    try {
      const r = await runDriveImport((token) =>
        discoveryApi.importAttachmentLink(discoveryId, url, token),
      );
      setView((v) => (v ? { ...v, discovery: { ...v.discovery, attachments: r.attachments } } : v));
      setLinkUrl("");
    } catch (err) {
      setDocErr((err as Error).message);
    } finally {
      setImporting(false);
    }
  }

  async function onRemoveAttachment(fileId: string) {
    try {
      const r = await discoveryApi.removeAttachment(discoveryId, fileId);
      setView((v) => (v ? { ...v, discovery: { ...v.discovery, attachments: r.attachments } } : v));
    } catch (err) {
      setDocErr((err as Error).message);
    }
  }

  const companyName = discovery?.companyName ?? status?.profile?.name ?? "Company";

  if (running) {
    return (
      <div style={{ padding: 24 }}>
        <DiscoveryProgress companyName={companyName} steps={status?.steps ?? []} />
      </div>
    );
  }

  if (errored || !profile) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-1)" }}>
          Couldn't research {companyName}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 8 }}>
          {status?.error ?? "Try the exact company name or its website."}
        </div>
        <div style={{ marginTop: 16 }}>
          <Button onClick={() => nav({ to: "/discovery" })}>Back to Discovery</Button>
        </div>
      </div>
    );
  }

  const competitors: CompetitorRef[] =
    profile.competitorDetails && profile.competitorDetails.length > 0
      ? profile.competitorDetails
      : profile.competitors.map((name) => ({ name }));
  const news = profile.news ?? [];
  const gcpAngle = profile.gcpOpportunities?.[0] ?? profile.engagementContext;

  const facts: { icon: React.ReactNode; label: string; value: string }[] = [];
  if (profile.employees)
    facts.push({ icon: <Users size={13} />, label: "employees", value: profile.employees });
  if (profile.openPositions)
    facts.push({
      icon: <Inbox size={13} />,
      label: "open roles",
      value: String(profile.openPositions),
    });
  if (profile.yearEstablished)
    facts.push({ icon: <Bolt size={13} />, label: "founded", value: profile.yearEstablished });
  if (profile.annualRevenue)
    facts.push({ icon: <Chart size={13} />, label: "revenue", value: profile.annualRevenue });
  if (profile.funding?.totalRaised)
    facts.push({ icon: <Trend size={13} />, label: "raised", value: profile.funding.totalRaised });
  if (profile.marketSize)
    facts.push({ icon: <Globe size={13} />, label: "market", value: profile.marketSize });
  if (profile.hq) facts.push({ icon: <Globe size={13} />, label: "", value: profile.hq });

  const TABS: { value: Tab; label: string }[] = [
    { value: "overview", label: "Overview" },
    { value: "intel", label: "Research & Billing" },
    { value: "notes", label: "Notes" },
    { value: "docs", label: "Docs" },
  ];

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "20px 20px 60px" }}>
      {/* At-a-glance summary card */}
      <div className="disc-summary">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <CompanyLogo
            name={profile.name}
            domain={profile.website}
            logoUrl={profile.logoUrl}
            size={64}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="disc-hero-name">{profile.name}</h1>
            <div className="disc-hero-links">
              {profile.industry && <span className="ent-pill">{profile.industry}</span>}
              {profile.website && (
                <a
                  href={
                    profile.website.startsWith("http")
                      ? profile.website
                      : `https://${profile.website}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="disc-hero-link"
                >
                  <Globe size={13} /> {hostOf(profile.website)}
                </a>
              )}
              {profile.linkedinUrl && (
                <a
                  href={safeHref(profile.linkedinUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="disc-hero-link"
                >
                  <LinkIcon size={13} /> LinkedIn
                </a>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
            <Button variant="ghost" onClick={() => void start("setup")} disabled={starting}>
              Set up later
            </Button>
            <Button onClick={() => void start("live")} disabled={starting}>
              {starting ? "Starting…" : "Start meeting"}
            </Button>
          </div>
        </div>

        {profile.overview && (
          <p
            style={{ margin: "14px 0 0", fontSize: 13.5, color: "var(--text-2)", lineHeight: 1.6 }}
          >
            {profile.overview}
          </p>
        )}

        {facts.length > 0 && (
          <div className="disc-summary-facts">
            {facts.map((f) => (
              <Fact key={f.label + f.value} icon={f.icon} label={f.label} value={f.value} />
            ))}
          </div>
        )}

        {gcpAngle && (
          <div className="disc-gcp-callout">
            <Spark size={16} style={{ flex: "0 0 auto", marginTop: 1 }} />
            <span>
              <strong>Google Cloud angle: </strong>
              {gcpAngle}
            </span>
          </div>
        )}
      </div>

      <div style={{ margin: "0 0 16px", overflowX: "auto" }}>
        <SegmentedControl<Tab> options={TABS} value={tab} onChange={setTab} size="sm" />
      </div>

      {tab === "overview" && (
        <>
          {discovery?.peopleNotes && (
            <Section title="People in the meeting" icon={<User size={15} />}>
              <div
                style={{
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: "var(--text-2)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {discovery.peopleNotes}
              </div>
            </Section>
          )}

          {news.length > 0 && (
            <Section title="Recent news" icon={<Notebook size={15} />}>
              <NewsList items={news} />
            </Section>
          )}

          {profile.founders.length > 0 && (
            <Section title="Founders & leadership" icon={<User size={15} />}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: 16,
                }}
              >
                {profile.founders.map((f) => (
                  <div key={f.name} style={{ display: "flex", gap: 12 }}>
                    <FounderAvatar name={f.name} photoUrl={f.photoUrl} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>
                        {f.name}
                        {f.role ? (
                          <span style={{ fontWeight: 400, color: "var(--text-3)" }}>
                            {" "}
                            · {f.role}
                          </span>
                        ) : null}
                      </div>
                      {f.background && (
                        <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>
                          {f.background}
                        </div>
                      )}
                      {f.recentMention && (
                        <div className="disc-recent">
                          <Notebook size={12} /> {f.recentMention}
                        </div>
                      )}
                      {f.linkedin && (
                        <a
                          href={safeHref(f.linkedin)}
                          target="_blank"
                          rel="noreferrer"
                          className="disc-hero-link"
                          style={{ marginTop: 4 }}
                        >
                          <LinkIcon size={12} /> LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {competitors.length > 0 && (
            <Section title="Competitors" icon={<Users size={15} />}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 10,
                }}
              >
                {competitors.map((c) => (
                  <div key={c.name} className="disc-comp-card">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <CompanyLogo
                        name={c.name}
                        domain={c.domain}
                        logoUrl={c.logoUrl}
                        size={32}
                        rounded={8}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--text-1)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {c.name}
                        </div>
                        {c.domain && (
                          <a
                            href={`https://${c.domain}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 11.5, color: "var(--gc-blue)" }}
                          >
                            {c.domain}
                          </a>
                        )}
                      </div>
                    </div>
                    {c.description && (
                      <p
                        style={{
                          margin: "8px 0 0",
                          fontSize: 12.5,
                          color: "var(--text-3)",
                          lineHeight: 1.45,
                        }}
                      >
                        {c.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {(profile.marketSize || (profile.marketTrends?.length ?? 0) > 0) && (
            <Section title="Market & trends" icon={<Trend size={15} />}>
              {profile.marketSize && (
                <p style={{ margin: "0 0 8px", fontSize: 13.5, color: "var(--text-2)" }}>
                  <strong>Market size:</strong> {profile.marketSize}
                </p>
              )}
              {profile.marketTrends && profile.marketTrends.length > 0 && (
                <Bullets items={profile.marketTrends} />
              )}
            </Section>
          )}

          {profile.funding &&
            (profile.funding.totalRaised || profile.funding.rounds.length > 0) && (
              <Section title="Funding" icon={<Trend size={15} />}>
                {profile.funding.totalRaised && (
                  <p
                    style={{
                      margin: "0 0 8px",
                      fontSize: 14,
                      color: "var(--text-1)",
                      fontWeight: 600,
                    }}
                  >
                    {profile.funding.totalRaised}
                  </p>
                )}
                {profile.funding.rounds.length > 0 && (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}
                  >
                    {profile.funding.rounds.map((r, i) => (
                      <div
                        key={`${r.round ?? i}-${r.date ?? ""}`}
                        style={{ fontSize: 13, color: "var(--text-2)" }}
                      >
                        <strong>{r.round ?? "Round"}</strong>
                        {r.amount ? ` · ${r.amount}` : ""}
                        {r.date ? ` · ${r.date}` : ""}
                        {r.leadInvestors?.length ? ` · led by ${r.leadInvestors.join(", ")}` : ""}
                      </div>
                    ))}
                  </div>
                )}
                {profile.funding.investors.length > 0 && (
                  <Chips items={profile.funding.investors} />
                )}
              </Section>
            )}

          {profile.gcpOpportunities && profile.gcpOpportunities.length > 0 && (
            <Section title="Google Cloud opportunities" icon={<Spark size={15} />}>
              <Bullets items={profile.gcpOpportunities} />
            </Section>
          )}
          {(profile.engagementContext || profile.cloudChallenges.length > 0) && (
            <Section title="Engagement angle & challenges" icon={<Bolt size={15} />}>
              {profile.engagementContext && (
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: 13.5,
                    color: "var(--text-2)",
                    lineHeight: 1.6,
                  }}
                >
                  {profile.engagementContext}
                </p>
              )}
              {profile.cloudChallenges.length > 0 && <Bullets items={profile.cloudChallenges} />}
            </Section>
          )}

          {profile.openPositions ? (
            <Section title="Hiring signal" icon={<Inbox size={15} />}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div className="disc-bignum">{profile.openPositions}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)" }}>
                    open positions right now
                  </div>
                  {profile.openPositionsUrl && (
                    <a
                      href={safeHref(profile.openPositionsUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="disc-hero-link"
                    >
                      <Globe size={12} /> View careers page
                    </a>
                  )}
                </div>
              </div>
            </Section>
          ) : null}

          {profile.techStack.length > 0 && (
            <Section title="Tech stack" icon={<Bolt size={15} />}>
              <Chips items={profile.techStack} />
            </Section>
          )}

          {profile.sources.length > 0 && (
            <Section title="Sources" icon={<Doc size={15} />}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {profile.sources.map((s) => (
                  <a
                    key={s.href}
                    href={safeHref(s.href)}
                    target="_blank"
                    rel="noreferrer"
                    className="disc-news-item"
                  >
                    <Favicon url={s.href} size={16} />
                    <div className="disc-news-title" style={{ fontSize: 12.5 }}>
                      {s.title || s.source}
                    </div>
                  </a>
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      {tab === "intel" && (
        <div className="card" style={{ padding: 12 }}>
          <CompanyReportPanel companyName={profile.name} researchMarkdown={profile.markdown} />
        </div>
      )}

      {tab === "notes" && (
        <Section title="Your notes" icon={<Notebook size={15} />}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--text-3)",
              marginBottom: 4,
            }}
          >
            About the company (free text)
          </label>
          <textarea
            className="setup-input"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            onBlur={saveNotes}
            rows={3}
            placeholder="Anything you know or want to remember about this company…"
            style={{ width: "100%", resize: "vertical", marginBottom: 12 }}
          />
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--text-3)",
              marginBottom: 4,
            }}
          >
            Personal notes
          </label>
          <textarea
            className="setup-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={saveNotes}
            rows={5}
            placeholder="Your prep notes for the meeting…"
            style={{ width: "100%", resize: "vertical" }}
          />
          <div style={{ fontSize: 11.5, color: "var(--text-4)", marginTop: 6 }}>
            Saved automatically.
          </div>
        </Section>
      )}

      {tab === "docs" && (
        <Section title="Context documents" icon={<Doc size={15} />}>
          <p style={{ fontSize: 13, color: "var(--text-3)", marginTop: 0 }}>
            Attach docs, goals or notes about this company — they'll carry into a meeting you start
            from here to help you prepare.
          </p>
          <input
            ref={fileRef}
            type="file"
            multiple
            onChange={onUpload}
            style={{ display: "none" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <Button onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? "Uploading…" : "Upload documents"}
            </Button>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
            <input
              className="input"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="…or paste a Google Drive/Docs link"
              value={linkUrl}
              disabled={importing}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void onImportLink();
                }
              }}
            />
            <Button onClick={() => void onImportLink()} disabled={importing}>
              {importing ? "Importing…" : "Import from Drive"}
            </Button>
          </div>
          {docErr && (
            <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--danger, #c00)" }}>
              {docErr}
            </div>
          )}
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
            {(discovery?.attachments ?? []).map((f) => (
              <div
                key={f.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  color: "var(--text-2)",
                }}
              >
                <Doc size={14} />
                {f.sourceUrl ? (
                  <a href={f.sourceUrl} target="_blank" rel="noopener noreferrer">
                    {f.name}
                  </a>
                ) : (
                  <span>{f.name}</span>
                )}
                <button
                  type="button"
                  className="ghost-btn xs danger"
                  style={{ marginLeft: "auto" }}
                  onClick={() => void onRemoveAttachment(f.id)}
                >
                  Remove
                </button>
              </div>
            ))}
            {(discovery?.attachments?.length ?? 0) === 0 && (
              <div style={{ fontSize: 12.5, color: "var(--text-4)" }}>
                No documents attached yet.
              </div>
            )}
          </div>
        </Section>
      )}

      {starting && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
            color: "var(--text-3)",
            fontSize: 13,
          }}
        >
          <Spinner size={14} /> Starting the meeting…
        </div>
      )}
    </div>
  );
}
